// src/api/api.js

const BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');
import { safeLocalStorage } from '@/lib/safe-storage'

const getToken = () => safeLocalStorage.getItem('auth_token');
const jsonHeadersWithToken = () => ({
  'Content-Type': 'application/json',
  ...(getToken() ? { 'x-access-token': getToken() } : {}),
});

// Function to call the API and fetch domain suggestions
export const searchDomains = async (searchQuery, page = 1, limit = 10, category = null) => {
  const response = await fetch(`${BASE_URL}/domains-v2/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      search_query: searchQuery,
      page,
      limit,
      ...(category ? { category } : {}),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch domain suggestions');
  }

  return response.json(); // Return the JSON response with pagination info
};

// Function to get domain price
export const getDomainPrice = async (domain) => {
  const response = await fetch(`${BASE_URL}/domains-v2/${domain}/price`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch domain price');
  }

  const data = await response.json();

  // Handle new API response format with prices object
  if (data.success && data.prices) {
    return {
      success: true,
      domain: data.domain,
      price: parseFloat(data.price), // Base price for 1 year
      currency: data.currency,
      responseCode: data.responseCode,
      responseText: data.responseText,
      prices: data.prices // Object with prices for 1-10 years
    };
  }

  return data; // Return original response for backward compatibility
};

// Function to revoke domain
export const revokeDomain = async (domain, notes = '') => {
  if (!domain) throw new Error('domain is required')

  const response = await fetch(`${BASE_URL}/domains-v2/${domain}/revoke`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({ notes }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || errorData.message || 'Failed to revoke domain')
  }

  return response.json()
};

// Function to delete third-party domain
export const deleteThirdPartyDomain = async (userId, domain) => {
  if (!userId || !domain) throw new Error('userId and domain are required')

  const response = await fetch(`${BASE_URL}/third-party-domains/${encodeURIComponent(userId)}/?domain=${encodeURIComponent(domain)}`, {
    method: 'DELETE',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || errorData.message || 'Failed to delete third-party domain')
  }

  return response.json()
};

// Function to register domain
export const registerDomain = async (registrationData) => {
  const response = await fetch(`${BASE_URL}/registration/register`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify(registrationData),
  });

  if (!response.ok) {
    throw new Error('Failed to register domain');
  }

  return response.json(); // Return the JSON response
};



// Workspace: domains purchased via CodeDesign (DomainWithNs)
export const getWorkspaceDomainsWithNs = async (workspaceId) => {
  const token = safeLocalStorage.getItem('auth_token')
  const response = await fetch(`${BASE_URL}/workspaces/${workspaceId}/domains-with-ns`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'x-access-token': token } : {}),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch workspace domains')
  }

  return response.json();
};

// Domainwithns API
export const addDomainWithNs = async (domainData, project, workspaceId) => {
  const { name, expiry_date } = domainData;

  if (!name || !expiry_date) {
    throw new Error('Domain name and expiry date are required');
  }

  // Ensure we only send necessary fields, not rawDomain or verification fields
  const cleanDomainData = {
    name,
    expiry_date
  };

  const response = await fetch(`${BASE_URL}/domains-with-ns/${workspaceId}`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      domainData: cleanDomainData,
      project,
      workspaceId
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to add domain' }));
    throw new Error(errorData.error || errorData.message || 'Failed to add domain with nameservers');
  }

  return response.json();
}

export const checkDomainWithNs = async (domain, workspaceId) => {
  const response = await fetch(`${BASE_URL}/domains-with-ns/${workspaceId}/check-status?domain=${encodeURIComponent(domain)}`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to check domain status' }));
    throw new Error(errorData.error || errorData.message || 'Failed to check domain status');
  }

  return response.json();
};

// Delete domain with nameservers
export const deleteDomainWithNs = async (domain, workspaceId) => {
  const url = `${BASE_URL}/domains-with-ns/${workspaceId}?domain=${encodeURIComponent(domain)}`;
  console.log('Delete domain API call:', { url, domain, workspaceId });

  const response = await fetch(url, {
    method: 'DELETE',
    headers: jsonHeadersWithToken(),
  });

  //console.log('Delete domain response status:', response.status);
  // console.log('Delete domain response ok:', response.ok);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to delete domain' }));
    console.log('Delete domain error data:', errorData);
    throw new Error(errorData.error || errorData.message || 'Failed to delete domain');
  }

  // Handle case where API returns just 'true' or other simple responses
  const responseText = await response.text();
  console.log('Delete domain response text:', responseText);

  try {
    const parsed = JSON.parse(responseText);
    console.log('Delete domain parsed response:', parsed);
    return parsed;
  } catch {
    // If response is not JSON (like 'true'), return a success object
    console.log('Delete domain response is not JSON, treating as success');
    return { success: true, message: 'Domain deleted successfully' };
  }
};




// Add a domain for a user; project is optional per latest docs (workspace-level when omitted)
export const addUserDomain = async (userId, { name, project, workspaceId } = {}) => {
  const response = await fetch(`${BASE_URL}/third-party-domains/${encodeURIComponent(userId)}/`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      name,
      ...(project ? { project } : {}),
      ...(workspaceId ? { workspaceId } : {}),
    }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to add user domain');
  }

  return response.json();
};

// Function to connect verified third-party domain to project
export const connectUserDomainToProject = async (userId, domain, project) => {
  const response = await fetch(`${BASE_URL}/third-party-domains/${encodeURIComponent(userId)}/connect`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      domain: domain,
      project: project
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'Failed to connect domain to project';

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const result = await response.json();

  // Check if the response contains an error field
  if (result.error === true) {
    const errorMessage = result.message || result.errorMessage || 'Domain connection failed';
    throw new Error(errorMessage);
  }

  return result;
};

// Check domain status (TXT/A verification)
export const checkUserDomainStatus = async (userId, domain) => {
  const response = await fetch(`${BASE_URL}/third-party-domains/${encodeURIComponent(userId)}/check-status?domain=${encodeURIComponent(domain)}`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to check domain status');
  }

  return response.json();
};

// // List user domains, filtered by project uniqueUrl or workspaceId
export const listUserDomains = async (userId, { project, workspaceId, verifiedDomainsOnly, offset, limit } = {}) => {
  const params = new URLSearchParams();
  if (project) params.set('project', project);
  if (workspaceId) params.set('workspaceId', workspaceId);
  if (verifiedDomainsOnly !== undefined) params.set('verifiedDomainsOnly', String(verifiedDomainsOnly));
  if (offset !== undefined) params.set('offset', String(offset));
  if (limit !== undefined) params.set('limit', String(limit));

  const url = `${BASE_URL}/third-party-domains/${encodeURIComponent(userId)}/list${params.toString() ? `?${params.toString()}` : ''}`;
  console.log('🔍 listUserDomains API call:', url);
  console.log('🔍 Parameters:', { userId, project, workspaceId, verifiedDomainsOnly, offset, limit });

  const response = await fetch(url, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    // console.error('❌ listUserDomains API error:', response.status, text);
    throw new Error(text || 'Failed to list user domains');
  }

  const result = await response.json();
  console.log('🔍 listUserDomains API response:', result);
  return result;
};

// // Public check (optional helper)
export const checkPublicDomainStatus = async (domain) => {
  const response = await fetch(`${BASE_URL}/third-party-domains/public/check-domain-status?domain=${encodeURIComponent(domain)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to check public domain status');
  }

  return response.json();
};

// Function to get domain detailed info
export const getDomainInfo = async (domain) => {
  const response = await fetch(`${BASE_URL}/domains-v2/${domain}/info?type=all_info`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch domain info');
  }

  return response.json(); // Return the JSON response
};

// Function to get domain detailed JSON info (with contacts, nameservers, etc.)
export const getDomainInfoJson = async (domain) => {
  const response = await fetch(`${BASE_URL}/domains-v2/${domain}/info?type=all_info`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch domain detailed info');
  }

  return response.json(); // Return the JSON response
};

// Function to get DNS records for a domain
export const getDNSRecords = async (domain) => {
  const response = await fetch(`${BASE_URL}/dns/${domain}/records`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch DNS records');
  }

  const result = await response.json();
  console.log('🔍 getDNSRecords API response:', result);
  console.log('🔍 Records TTL values:', result?.data?.records?.map(r => ({ type: r.type, ttl: r.ttl, TTL: r.TTL })));
  return result;
};

// Function to add DNS records for a domain
export const addDNSRecords = async (domain, records) => {
  const response = await fetch(`${BASE_URL}/dns/${domain}/records`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      records
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to add DNS records');
  }

  return response.json(); // Return the JSON response
};

// Function to update a DNS record for a domain
export const updateDNSRecord = async (domain, match, update) => {
  const payload = {
    match,
    update
  };

  console.log('🔍 updateDNSRecord - Sending payload:', JSON.stringify(payload, null, 2));
  console.log('🔍 updateDNSRecord - TTL value being sent:', update.ttl);
  console.log('🔍 updateDNSRecord - Full update object:', update);

  const response = await fetch(`${BASE_URL}/dns/${domain}/records/single`, {
    method: 'PATCH',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to update DNS record' }));
    throw new Error(errorData.error || errorData.message || 'Failed to update DNS record');
  }

  const result = await response.json();
  console.log('✅ updateDNSRecord API response:', result);
  console.log('✅ Response update TTL:', result?.data?.update?.ttl);
  return result;
};

// Function to delete a DNS record for a domain
export const deleteDNSRecord = async (domain, match) => {
  const response = await fetch(`${BASE_URL}/dns/${domain}/records/single`, {
    method: 'DELETE',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      match
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to delete DNS record');
  }

  return response.json(); // Return the JSON response
};

// Function to update domain contacts
export const updateDomainContacts = async (domain, contactData) => {
  const response = await fetch(`${BASE_URL}/domains-v2/${domain}/contacts`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      contactSet: contactData
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update domain contacts');
  }

  return response.json(); // Return the JSON response
};

// Function to modify domain settings (auto-renew, whois privacy, etc.)
export const modifyDomainSettings = async (domain, settings) => {

  const response = await fetch(`${BASE_URL}/domains-v2/${domain}/modify`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify(settings),
  });


  if (!response.ok) {
    throw new Error('Failed to modify domain settings');
  }

  const result = await response.json();
  return result;
};

// Function to update domain auto-renew setting
export const updateDomainAutoRenew = async (domain, autoRenew) => {
  const response = await fetch(`${BASE_URL}/domains-v2/${domain}/auto-renew`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      autoRenew: autoRenew, // boolean
      letExpire: !autoRenew  // inverse of autoRenew
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to update auto-renew settings');
  }

  const result = await response.json();
  return result;
};

// Function to update nameservers for a domain
export const updateNameservers = async (domain, nameservers) => {
  const response = await fetch(`${BASE_URL}/nameservers/update`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      domain: domain,
      op_type: 'assign',
      nameservers: nameservers.map(ns => ({
        name: ns.name || ns,
        ...(ns.ipaddress && { ipaddress: ns.ipaddress })
      }))
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update nameservers');
  }

  return response.json(); // Return the JSON response
};

// Function to get WHOIS privacy state for a domain
export const getWhoisPrivacyState = async (domain) => {
  const response = await fetch(`${BASE_URL}/domains-v2/${domain}/info?type=whois_privacy_state`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch WHOIS privacy state');
  }

  return response.json(); // Return the JSON response
};

// Function to update WHOIS privacy state for a domain
export const updateWhoisPrivacy = async (domain, state) => {
  console.log('🔧 updateWhoisPrivacy - Domain:', domain);
  console.log('🔧 updateWhoisPrivacy - State:', state);

  const response = await fetch(`${BASE_URL}/domains-v2/${domain}/whois-privacy`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      state: state // "enable" or "disable"
    }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || errorData.message || 'Failed to update WHOIS privacy');
  }

  return response.json();
};

// Function to get third-party domain details
export const getThirdPartyDomainDetails = async (userId, domain) => {
  const response = await fetch(`${BASE_URL}/third-party-domains/${encodeURIComponent(userId)}?domain=${encodeURIComponent(domain)}`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'Failed to fetch domain details';

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  return response.json();
};

// Function to disconnect third-party domain from project
export const disconnectThirdPartyDomain = async (userId, domain) => {
  const response = await fetch(`${BASE_URL}/third-party-domains/${encodeURIComponent(userId)}/disconnect`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      domain: domain
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'Failed to disconnect domain from project';

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const result = await response.json();

  // Check if the response contains an error field
  if (result.error === true) {
    const errorMessage = result.message || result.errorMessage || 'Domain disconnection failed';
    throw new Error(errorMessage);
  }

  return result;
};

// Function to disconnect withNS domain from project
export const disconnectDomainWithNsFromProject = async (workspaceId, domain) => {
  // Extract domain name if domain is an object, otherwise use domain as string
  const domainName = typeof domain === 'string' ? domain : domain.name;

  const response = await fetch(`${BASE_URL}/domains-with-ns/${workspaceId}/disconnect`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({
      domain: domainName,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'Failed to disconnect domain from project';

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const result = await response.json();

  // Check if the response contains an error field
  if (result.error === true) {
    const errorMessage = result.message || result.errorMessage || 'Domain disconnection failed';
    throw new Error(errorMessage);
  }

  return result;
};


